/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Campaign Page component
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.12.0
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { makeSelectorBannerTotalCount } from '@/main/modules/banner/bannerSelectors';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { LinkBlock, PageTitle, SideMenu, SideMenuItem } from '@components/common';
import { Counter } from '@components/common/PageTitle/styled/PageTitleStyled';
import type { ISideMenuItem } from '@components/common/SideMenu/SideMenuItem';
import Title from '@components/common/Title/Title';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectPageCampaignsTranslation } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { RoutePath } from '@routes/constants/routesConstants';

import { makeSelectFeatureEnabled, makeSelectIsSubscriber } from '../../PageAuth/selectors/authSelectors';
import { ECampaignsType } from '../types';
import type { ICampaignsRouteFilter } from '../types';
import { useCheckOnCampaignCount } from '../utils/useCheckOnCampaignCount';
import { CampaignsPageStyled } from './styled/CampaignsPageStyled';
import CampaignsTable from './Table/CampaignsTable';

interface IPageCampaignsMenuItem extends Pick<ISideMenuItem, 'title'> {
    count: number;
    id: ECampaignsType;
    url: string;
}

const CampaignsPage = () => {

    const location = useLocation<{ filter: ICampaignsRouteFilter }>();
    const history = useHistory();

    const { accountCount, subscriberCount } = useCheckOnCampaignCount();
    const bannerTotalCount = useSelector(makeSelectorBannerTotalCount());
    const isCampaignsImprovementsFeature = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT));

    useLayoutEffect(() => {
        if (!bannerTotalCount && isCampaignsImprovementsFeature) {
            openRoute(RoutePath.dashboard);
        }
    }, [bannerTotalCount, isCampaignsImprovementsFeature]);

    useEffect(() => {
        if (!accountCount && subscriberCount) {
            history.replace(RoutePath.campaignsSubscriber)
        }
    }, [accountCount, history, subscriberCount]);

    const [currentType, setCurrentType] = useState<ECampaignsType | undefined>();

    const isSubscriber = useSelector(makeSelectIsSubscriber());
    const translationCampaigns = useSelector(makeSelectPageCampaignsTranslation());

    const sideMenuItems = useMemo((): IPageCampaignsMenuItem[] => [
        {
            count: accountCount,
            id: ECampaignsType.account,
            title: translationCampaigns.sideMenu.account,
            url: RoutePath.campaignsAccount,
        },
        {
            count: subscriberCount,
            id: ECampaignsType.subscriber,
            title: translationCampaigns.sideMenu.subscriber,
            url: RoutePath.campaignsSubscriber,
        },
    ].filter(item => item.count),
        [accountCount, subscriberCount, translationCampaigns],
    );

    const renderSideMenuItem = useCallback((item: IPageCampaignsMenuItem, level = 0) => (
        <LinkBlock key={item.url} url={item.url}>
            <SideMenuItem
                id={item.url}
                isActive={item.id === currentType}
                level={level}
                title={
                    <Fragment>
                        <div className="text-block">{item.title}</div>
                        <Counter>{item.count}</Counter>
                    </Fragment>
                }
            />
        </LinkBlock>
    ), [currentType]);

    useEffect(() => {
        if (location.pathname === RoutePath.campaignsAccount && accountCount) {
            setCurrentType(ECampaignsType.account);
        } else if (location.pathname === RoutePath.campaignsSubscriber) {
            setCurrentType(ECampaignsType.subscriber);
        }
    }, [accountCount, location.pathname]);

    return (
        <Fragment>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageCampaigns]} />

            <Title pageName={ECommonTranslationPages.campaigns} />

            <CampaignsPageStyled isSubscriber={isSubscriber}>
                <div className="campaign__title">
                    <PageTitle
                        count={accountCount + subscriberCount}
                        title={translationCampaigns.title.label}
                    />
                </div>
                {
                    !isSubscriber && (
                        <div className="campaign__menu">
                            <SideMenu>{sideMenuItems.map(renderSideMenuItem)}</SideMenu>
                        </div>
                    )
                }
                <div className="campaign__content">
                    {!!currentType && !!(accountCount || subscriberCount) && <CampaignsTable
                        type={currentType}
                    />}
                </div>
            </CampaignsPageStyled>
        </Fragment>
    );
};

export default CampaignsPage;
